import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ChatterCommand, ChatterCommandList, ChatterCommandType, ChatterInputType } from './chatter.model';
import { Subscription } from 'rxjs';
import { UtilService } from '../../services/util/util.service';
import { ContentService } from '../../services/content/content.service';

const content = require('./chatter.content.json');

@Component({
  selector: 'app-chatter',
  templateUrl: './chatter.component.html',
  styleUrls: ['./chatter.component.scss']
})
export class ChatterComponent implements OnInit, OnDestroy {
  @ViewChild('scrollContainer', { static: true, read: ElementRef })
  private scrollContainer: ElementRef;

  @Input()
  public commandsList: ChatterCommandList;

  @Input()
  public providedData: any;

  @Output()
  public flowResult: EventEmitter<any> = new EventEmitter<any>();

  public totalUserCommands: number = 0;
  public currentIndex: number = 0;
  public responseMap: any = {};
  public activeControls: ChatterInputType;
  public activeSet: any[];
  public currentModalBlueprint: any;
  public finalCommand: ChatterCommand;
  public commandsToRender: ChatterCommand[] = [];
  public responseItem: any;
  public memory: any = {};
  public isMobile: boolean = false;
  public subscription: Subscription;
  public currentCommand: ChatterCommand;
  public percentage: number = 0;
  public lastResponse: any;
  public hasMultipleEndings: boolean;
  public notFound: boolean = false;
  public nameFromEntitySelector: any;

  public constructor(
    private utilService: UtilService,
    private contentService: ContentService
  ) {
    contentService.loadContent(content);
    this.subscription = utilService.getScreenMode$().subscribe((screenMode: any) => {
      this.isMobile = screenMode.includes('MOBILE');
    });
  }

  public ngOnInit(): void {
    this.responseMap = this.extractVariablesFromCommands(this.commandsList.commands);
    this.setGlobalChatterConfiguration();
    this.commandsToRender.push(this.commandsList.commands[this.currentIndex]);
    this.totalUserCommands = this.commandsList.commands.length;
  }

  public setGlobalChatterConfiguration(): void {
    if (this.commandsList.commands.filter(item => item.isFinalStep).length === 1) {
      this.finalCommand = this.commandsList.commands[this.commandsList.commands.length - 1];
      this.hasMultipleEndings = false;
    } else {
      this.hasMultipleEndings = true;
    }
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public calculateProgress(): void {
    this.percentage = Math.floor((100 / this.totalUserCommands) * this.currentIndex);
  }

  public next(): void {
    this.currentIndex++;
    this.currentCommand = this.setCurrentCommand();

    if (this.currentCommand) {
      this.setControlsForActiveCommand();
      this.processStep();
    }
    this.scrollToBottom();
    this.calculateProgress();
  }

  public setControlsForActiveCommand(): void {
    this.setActiveControls(this.currentCommand.expectedInput);
    this.handleProvidedInput();
    this.setCurrentModalBlueprint(this.currentCommand?.modalBlueprint);
  }

  private handleProvidedInput(): void {
    this.currentCommand.expectedInput === ChatterInputType.FROM_PROVIDED_DATA
      ? this.setActiveSet(this.providedData[this.currentCommand.dataKey])
      : this.setActiveSet(this.currentCommand.providedInput);
  }

  public processStep(): void {
    if (this.currentCommand.conditions) {
      this.processStepWithConditions();
    } else if (this.currentCommand.type === ChatterCommandType.SYSTEM) {
      this.processStepWithoutConditions();
    }
  }

  public processStepWithConditions(): void {
    const conditionsMet: boolean = this.determineIfStepConditionsAreMet();
    if (conditionsMet) {
      if (this.hasMultipleEndings && this.currentCommand.isFinalStep) {
        this.finalCommand = this.currentCommand;
      }
      if (this.currentCommand.type === ChatterCommandType.SYSTEM) {
        this.commandsToRender.push(this.currentCommand);
      }
    } else {
      this.next();
    }
  }

  public processStepWithoutConditions(): void {
    if (this.hasMultipleEndings) {
      this.finalCommand = this.currentCommand;
    }
    this.commandsToRender.push(this.commandsList.commands[this.currentIndex]);
  }

  public determineIfStepConditionsAreMet(): boolean {
    let conditionsAreMet: boolean = true;
    this.currentCommand.conditions.forEach(condition => {
      if (condition.type === 'isNotEmpty') {
        conditionsAreMet = this.handleIsNotEmptyAssertion(conditionsAreMet, condition);
      } else if (condition.type === 'equalsValue') {
        conditionsAreMet = this.handleEqualsValueAssertion(conditionsAreMet, condition);
      } else if (condition.type === 'isFalsy') {
        conditionsAreMet = this.handleIsFalsyAssertion(conditionsAreMet, condition);
      }
    });
    return conditionsAreMet;
  }

  private handleIsNotEmptyAssertion(result: boolean, condition: any): boolean {
    if (condition.propositionStrategy && condition.propositionStrategy === 'OR') {
      result = result || !!this.responseMap[condition.conditionField].data;
    } else {
      result = result && !!this.responseMap[condition.conditionField].data;
    }
    return result;
  }

  private handleEqualsValueAssertion(result: boolean, condition: any): boolean {
    return result && this.responseMap[condition.conditionField].data === condition.expectedValue;
  }

  private handleIsFalsyAssertion(result: boolean, condition: any): boolean {
    if (condition.propositionStrategy && condition.propositionStrategy === 'OR') {
      result = result || this.isFieldFalsy(condition);
    } else {
      result = result && this.isFieldFalsy(condition);
    }

    return result;
  }

  private isFieldFalsy(condition): boolean {
    return (
      this.responseMap[condition.conditionField].data === null ||
      this.responseMap[condition.conditionField].data === undefined ||
      this.responseMap[condition.conditionField].data === [] ||
      this.responseMap[condition.conditionField].data === {} ||
      this.responseMap[condition.conditionField].data === 0
    );
  }

  public setCurrentCommand(): ChatterCommand {
    return this.commandsList.commands[this.currentIndex];
  }

  public extractVariablesFromCommands(commandList: any): any {
    const variables: any = {};

    commandList.forEach((command: ChatterCommand) => {
      if (command.variableName) {
        this.memory[command.variableName] = null;
        variables[command.variableName] = { label: command.variableLabel, data: undefined };
      }
    });

    return variables;
  }

  public processResponse($event: any): void {
    this.notFound = false;
    if ($event === null) {
      this.currentCommand.message = this.currentCommand.skipMessage;
    } else if (
      this.currentCommand.expectedInput === ChatterInputType.MULTI_SELECT ||
      this.currentCommand.expectedInput === ChatterInputType.FROM_PROVIDED_DATA ||
      this.currentCommand.expectedInput === ChatterInputType.PROVIDED_SET
    ) {
      this.handleProvidedSetResponse($event);
    } else {
      this.handleSkipCommand($event);
    }

    if (this.lastResponse !== $event) {
      this.appendToResponseMap($event);
    }

    this.commandsToRender.push(this.currentCommand);
    this.responseItem = undefined;
    this.activeSet = [];
    this.lastResponse = $event;
  }

  private handleProvidedSetResponse($event): void {
    if (this.lastResponse !== $event) {
      if (
        this.currentCommand.expectedInput === ChatterInputType.MULTI_SELECT ||
        this.currentCommand.expectedInput === ChatterInputType.FROM_PROVIDED_DATA
      ) {
        let selectedItems: any = $event.filter(item => item.selected).map(item => item.name);
        selectedItems = selectedItems?.join(', ');
        this.currentCommand.message = selectedItems;
      } else {
        this.currentCommand.message = $event;
      }
    } else {
      this.currentCommand.message = this.currentCommand.skipMessage;
    }
  }

  private handleSkipCommand($event): void {
    if (this.lastResponse !== $event) {
      this.currentCommand.message = $event[this.currentCommand.responseLabel];
    } else {
      this.currentCommand.message = this.currentCommand.skipMessage;
    }
  }

  private appendToResponseMap($event): void {
    this.responseMap[this.currentCommand.variableName] = {
      ...this.responseMap[this.currentCommand.variableName],
      data: $event
    };
    this.memory[this.currentCommand.variableName] = $event;
  }

  public finalize(): void {
    if (this.responseMap.$functie && this.responseMap.$functie.data && !Array.isArray(this.responseMap.$functie.data)) {
      this.responseMap.$functie.data = [this.responseMap.$functie.data];
    } else if (
      this.responseMap.$zijInstroomFunctie &&
      this.responseMap.$zijInstroomFunctie.data &&
      !Array.isArray(this.responseMap.$zijInstroomFunctie.data)
    ) {
      this.responseMap.$zijInstroomFunctie.data = [this.responseMap.$zijInstroomFunctie.data];
    } else if (
      this.responseMap.$opleiding &&
      this.responseMap.$opleiding.data &&
      !Array.isArray(this.responseMap.$opleiding.data)
    ) {
      this.responseMap.$opleiding.data = [this.responseMap.$opleiding.data];
    } else if (
      this.responseMap.$opleidingRetry &&
      this.responseMap.$opleidingRetry.data &&
      !Array.isArray(this.responseMap.$opleidingRetry.data)
    ) {
      this.responseMap.$opleidingRetry.data = [this.responseMap.$opleidingRetry.data];
    }

    this.flowResult.emit({ responseMap: this.responseMap, data: this.providedData });
  }

  public getActualResponseMap(): any[] {
    const arr: any[] = [];
    Object.keys(this.responseMap).forEach(item => arr.push(this.responseMap[item]));
    return arr;
  }

  public setActiveControls(activeControl: ChatterInputType): void {
    setTimeout(() => {
      this.activeControls = activeControl;
    });
  }

  public setActiveSet(activeSet: any): void {
    setTimeout(() => {
      if (activeSet) {
        this.activeSet = activeSet;
      }
    });
  }

  public setCurrentModalBlueprint(blueprint: any): void {
    setTimeout(() => {
      if (blueprint) {
        this.currentModalBlueprint = blueprint;
      }
    });
  }

  public scrollToBottom(): void {
    try {
      this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
    } catch (err) {
    }
  }

  public extractButtonLabel(): string {
    return this.currentCommand?.variableName === '$opleiding' || this.currentCommand?.variableName === '$opleidingRetry'
      ? this.contentService.getContent('chatter.selectOpleidingButton')
      : this.contentService.getContent('chatter.selectFunctionButton');
  }

  public setResponseItemWithInterests(interests: Array<{ name: string; selected: boolean }>): void {
    this.responseItem = interests.find(interest => interest.selected) ? interests : undefined;
  }
}
